/**
 * Centralized Prompt Resolution Utility
 *
 * This utility handles:
 * 1. Recursive replacement of {{prompt_id}} placeholders from database
 * 2. Replacement of [[field_name]] placeholders with actual user data
 *
 * Can be used across Blueprint Builder, Report generation, and other features
 */

import { getAuth } from 'firebase/auth';

// In-memory cache for prompts to avoid redundant API calls
const promptCache = new Map<string, string>();

/**
 * Clear the prompt cache (useful when prompts are updated)
 */
export function clearPromptCache(): void {
  promptCache.clear();
}

/**
 * Clear a specific prompt from the cache
 */
export function clearPromptFromCache(promptId: string): void {
  promptCache.delete(promptId);
}

/**
 * Helper to get Firebase function URL based on environment
 * Inlined to avoid circular dependency
 */
function getFirebaseFunctionURL(functionName: string): string {
  // Check new environment localStorage key first
  const savedEnv = localStorage.getItem('leelalife-environment');
  const isDevEnvironment =
    savedEnv === 'development' ||
    window.location.href.includes('devMode=true') ||
    localStorage.getItem('devmode') === 'true';

  return isDevEnvironment
    ? `http://127.0.0.1:5001/subtle-cubist-419805/us-central1/${functionName}`
    : `https://${functionName}-gqjfwgmboa-uc.a.run.app`;
}

/**
 * Fetch a specific prompt by ID from the database
 */
async function fetchPromptById(promptId: string): Promise<string | null> {
  // Check cache first
  if (promptCache.has(promptId)) {
    return promptCache.get(promptId)!;
  }

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  try {
    const token = await user.getIdToken();
    const response = await fetch(`${getFirebaseFunctionURL('getPromptById')}?id=${promptId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.warn(`Prompt not found: ${promptId}`);
      return null;
    }

    const data = await response.json();
    if (data.prompt?.content) {
      // Cache the result
      promptCache.set(promptId, data.prompt.content);
      return data.prompt.content;
    }

    return null;
  } catch (error) {
    console.error(`Error fetching prompt ${promptId}:`, error);
    return null;
  }
}

/**
 * Extract all {{prompt_id}} placeholders from content
 */
function extractPromptIds(content: string): string[] {
  const promptPlaceholderRegex = /\{\{([a-zA-Z0-9_-]+)\}\}/g;
  const promptIds = new Set<string>();
  const matches = content.matchAll(promptPlaceholderRegex);

  for (const match of matches) {
    promptIds.add(match[1]);
  }

  return Array.from(promptIds);
}

/**
 * Recursively replace {{prompt_id}} placeholders with actual prompt content from database
 * Only fetches the prompts that are actually referenced
 */
export async function resolvePromptPlaceholders(
  content: string,
  maxIterations: number = 10
): Promise<string> {
  let result = content;
  let iteration = 0;

  while (iteration < maxIterations) {
    iteration++;

    // Extract all prompt IDs from current content
    const promptIds = extractPromptIds(result);

    if (promptIds.length === 0) {
      // No more prompts to resolve
      break;
    }

    // Fetch only the prompts we need (in parallel for efficiency)
    const promptFetches = promptIds.map(async (promptId) => {
      const promptContent = await fetchPromptById(promptId);
      return { promptId, promptContent };
    });

    const resolvedPrompts = await Promise.all(promptFetches);

    // Replace all found prompts in the content
    let hasReplacements = false;
    for (const { promptId, promptContent } of resolvedPrompts) {
      if (promptContent) {
        const placeholder = `{{${promptId}}}`;
        const newResult = result.split(placeholder).join(promptContent);

        if (newResult !== result) {
          hasReplacements = true;
          result = newResult;
        }
      }
    }

    // If no replacements were made, we're done
    if (!hasReplacements) {
      break;
    }
  }

  return result;
}

/**
 * Replace @@field_name@@ placeholders with actual user data
 */
export function replaceDataPlaceholders(content: string, userData: Record<string, any>): string {
  let result = content;

  // Regex to find @@field_name@@ patterns
  const dataPlaceholderRegex = /@@([a-zA-Z0-9_]+)@@/g;
  const matches = result.matchAll(dataPlaceholderRegex);

  for (const match of matches) {
    const fieldName = match[1];

    // Check if we have this field in userData
    if (
      fieldName in userData &&
      userData[fieldName] !== undefined &&
      userData[fieldName] !== null
    ) {
      const placeholder = `@@${fieldName}@@`;
      const value = String(userData[fieldName]);

      // Replace all occurrences
      result = result.split(placeholder).join(value);
    }
  }

  return result;
}

/**
 * Complete prompt resolution:
 * 1. Recursively resolve {{prompt_id}} from database
 * 2. Replace @@field_name@@ with user data
 */
export async function resolvePrompt(
  content: string,
  userData: Record<string, any> = {}
): Promise<string> {
  // Step 1: Resolve all database prompts recursively
  let result = await resolvePromptPlaceholders(content);

  // Step 2: Replace user data placeholders
  result = replaceDataPlaceholders(result, userData);

  return result;
}

/**
 * Helper to extract user data from assessment details for common use cases
 */
export function extractUserDataFromAssessment(assessmentDetails: any): Record<string, any> {
  const firstName = assessmentDetails?.personalDetails?.firstName || '';
  const lastName = assessmentDetails?.personalDetails?.lastName || '';

  // Generate initials from names
  const getInitials = (name: string): string => {
    if (!name) return '';
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  };

  const doshas = assessmentDetails?.doshas || assessmentDetails?.doshaValues;

  return {
    firstName,
    lastName,
    email: assessmentDetails?.email || '',
    dateOfBirth: assessmentDetails?.personalDetails?.dateOfBirth || '',
    timeOfBirth: assessmentDetails?.personalDetails?.timeOfBirth || '',
    placeOfBirth: assessmentDetails?.personalDetails?.placeOfBirth || '',
    clientInitials: getInitials(`${firstName} ${lastName}`),
    vata: doshas?.vata || 0,
    pitta: doshas?.pitta || 0,
    kapha: doshas?.kapha || 0,
  };
}
